import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Save,
  Camera,
  Check,
  User,
  Briefcase,
  Clock,
  Banknote,
  HelpCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../components/ui/popover";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";
import { SalaryBreakdown } from "../components/SalaryBreakdown";
import { OvertimeBreakdown, MonthlyPayEstimate } from "../components/SalaryBreakdown";
import { WorkScheduleEditor } from "../components/WorkScheduleEditor";
import { uploadFile } from "../lib/files";
import AuthImage from "../components/AuthImage";
import DatePickerEs from "../components/DatePickerEs";

// ──────────────────────────────────────────────────────────────────────────
// Step metadata — siempre 4 pasos. Para "ocasional" los campos opcionales
// se permiten vacíos (solo nombre + ocupación son obligatorios).
// ──────────────────────────────────────────────────────────────────────────
const STEPS_FULL = [
  { key: "personal", label: "Datos Personales", icon: User },
  { key: "contrato", label: "Naturaleza del Contrato", icon: Briefcase },
  { key: "condiciones", label: "Condiciones Laborales", icon: Clock },
  { key: "salario", label: "Salario y Forma de Pago", icon: Banknote },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  // Step 1
  worker_type: "permanent",
  photo_path: "",
  first_name: "",
  last_name_1: "",
  last_name_2: "",
  id_type: "CN",
  national_id: "",
  birth_date: "",
  birth_place: "",
  nationality: "CR",
  gender: "",
  marital_status: "soltero",
  education_level: "",
  address: "",
  phone: "",
  email: "",
  emergency_contact_name: "",
  emergency_contact_relationship: "conyuge",
  emergency_contact_phone: "",
  // Step 2
  hire_date: todayISO(),
  primary_branch_id: "",
  company_id: "",
  occupation_id: "",
  department_id: "",
  specific_labor: "",
  economic_activity: "COM",
  contract_type: "CTI",
  contract_reason: "",
  contract_end_date: "",
  // Step 3
  journey_type: "JOD",
  journey_mode: "continua",
  social_guarantees: { ccss: true, ins: true },
  work_schedule_type: "tiempo_completo",
  schedule_id: "",
  start_time: "07:00",
  end_time: "17:00",
  work_days: ["lunes", "martes", "miercoles", "jueves", "viernes"],
  day_off: "sabado",
  day_schedules: {
    lunes: { start: "07:00", end: "17:00" },
    martes: { start: "07:00", end: "17:00" },
    miercoles: { start: "07:00", end: "17:00" },
    jueves: { start: "07:00", end: "17:00" },
    viernes: { start: "07:00", end: "17:00" },
  },
  schedule_accepted: false,
  break_schedule: {
    breakfast: { minutes: 15 },
    lunch: { minutes: 30 },
    refrigerio: { minutes: 15 },
  },
  // Step 4
  payment_amount_tm: 0,
  min_salary: 0,
  base_salary: 0,
  occupation_salary_type: "monthly",
  salary_fixation: "monthly",
  payment_method: "EFE",
  payment_periodicity: "QUINCENA",
  bank_account: "",
  payment_location: "",
  authorized_deductions: ["GARANTIAS_SOCIALES"],
  other_deductions: [],
};

// Small helpers
const byCode = (list, code) => list.find((x) => x.code === code);

// ──────────────────────────────────────────────────────────────────────────
// Main Wizard
// ──────────────────────────────────────────────────────────────────────────
export default function EmployeeWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [catalogs, setCatalogs] = useState({});
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [payrollCfg, setPayrollCfg] = useState({ overtime_multiplier: 1.5 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isOccasional = form.worker_type === "occasional";
  const STEPS = STEPS_FULL;

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  // Load catalogs + config lists once
  useEffect(() => {
    (async () => {
      try {
        const [cat, br, co, dp, oc, sc] = await Promise.all([
          api.get("/catalogs"),
          api.get("/branches"),
          api.get("/companies"),
          api.get("/departments"),
          api.get("/occupations"),
          api.get("/schedules"),
        ]);
        setCatalogs(cat.data);
        setBranches(br.data);
        setCompanies(co.data);
        setDepartments(dp.data);
        setOccupations(oc.data);
        setSchedules(sc.data);
        api.get("/payroll-config").then(({ data }) => setPayrollCfg(data || {})).catch(() => {});

        // Pre-fill company with primary one if exists
        const primary = (co.data || []).find((c) => c.is_primary);
        if (primary) setForm((f) => ({ ...f, company_id: primary.id }));
      } catch {
        toast.error("Error al cargar catálogos");
      }
    })();
  }, []);

  // Auto-compute day_off
  useEffect(() => {
    const all = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
    const off = all.find((d) => !form.work_days.includes(d));
    if (off && off !== form.day_off) patch({ day_off: off });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.work_days]);

  const toggleWorkDay = (day) => {
    const curr = new Set(form.work_days);
    if (curr.has(day)) curr.delete(day);
    else curr.add(day);
    patch({ work_days: Array.from(curr) });
  };

  // Upload photo (works even before employee exists — we save path now, link on submit)
  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La foto no debe superar 5 MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const rec = await uploadFile(file, { category: "employee-photo" });
      patch({ photo_path: rec.storage_path });
      toast.success("Foto cargada");
    } catch {
      toast.error("Error al subir la foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Step validation — para "ocasional" se relajan validaciones: solo
  // requiere nombre + ocupación; el resto de campos son opcionales y
  // se permite avanzar / guardar dejándolos vacíos.
  const validateStep = () => {
    if (isOccasional) {
      if (step === 0) {
        if (!form.first_name.trim() && !form.last_name_1.trim()) {
          toast.error("Ingrese al menos nombre o primer apellido");
          return false;
        }
      }
      if (step === 1) {
        if (!form.occupation_id) {
          toast.error("Seleccione la ocupación");
          return false;
        }
      }
      // Pasos 2 y 3: sin validaciones obligatorias
      return true;
    }
    if (step === 0) {
      if (!form.first_name.trim() || !form.last_name_1.trim()) {
        toast.error("Nombre y primer apellido son requeridos");
        return false;
      }
      if (!form.national_id.trim()) {
        toast.error("Número de identificación requerido");
        return false;
      }
      if (!form.birth_date) {
        toast.error("Fecha de nacimiento requerida");
        return false;
      }
    }
    if (step === 1) {
      if (!form.hire_date) { toast.error("Fecha de ingreso requerida"); return false; }
      if (!form.primary_branch_id) { toast.error("Lugar de trabajo requerido"); return false; }
      if (!form.occupation_id) { toast.error("Ocupación requerida"); return false; }
    }
    if (step === 2) {
      if (form.work_days.length === 0) { toast.error("Seleccione al menos un día laboral"); return false; }
      const ds = form.day_schedules || {};
      const missing = form.work_days.filter((d) => !ds[d]?.start || !ds[d]?.end);
      if (missing.length) {
        toast.error("Defina la hora de entrada y salida en TODOS los días laborales");
        return false;
      }
    }
    return true;
  };

  const onNext = async () => {
    if (!validateStep()) return;
    // Validación temprana de cédula duplicada al salir del paso de Datos Personales
    if (!isOccasional && step === 0 && form.national_id.trim()) {
      try {
        const { data } = await api.get("/employees/check-cedula", {
          params: { national_id: form.national_id.trim() },
        });
        if (data?.exists) {
          toast.error(
            `Ya existe un trabajador con esta cédula: ${data.employee_code} - ${data.full_name}`
          );
          return;
        }
      } catch {
        // si la verificación falla, el backend igual bloquea al guardar (409)
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const onPrev = () => setStep((s) => Math.max(s - 1, 0));

  const buildFullName = () => {
    return [form.first_name, form.last_name_1, form.last_name_2]
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(" ");
  };

  const onSubmit = async () => {
    if (!validateStep()) return;
    const full_name = buildFullName();
    const allDays = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
    const ds = form.day_schedules || {};
    const firstDay = (form.work_days || [])[0];
    const derivedStart = firstDay && ds[firstDay]?.start ? ds[firstDay].start : form.start_time;
    const derivedEnd = firstDay && ds[firstDay]?.end ? ds[firstDay].end : form.end_time;
    const daysOff = allDays.filter((d) => !(form.work_days || []).includes(d));
    const payload = {
      ...form,
      full_name,
      economic_activity: "COM",
      salary_fixation: form.occupation_salary_type || "monthly",
      start_time: derivedStart,
      end_time: derivedEnd,
      days_off: daysOff,
      day_off: daysOff[0] || "",
      base_salary: Number(form.base_salary) || 0,
      min_salary: Number(form.min_salary) || 0,
      payment_amount_tm: Number(form.payment_amount_tm) || 0,
      department_id: form.department_id || null,
      occupation_id: form.occupation_id || null,
      company_id: form.company_id || null,
      schedule_id: form.schedule_id || null,
    };
    setSaving(true);
    try {
      const { data } = await api.post("/employees", payload);
      toast.success("Trabajador registrado");
      navigate(`/empleados/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div data-testid="employee-wizard-page" className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/empleados")}
          className="text-zinc-500 hover:text-zinc-900 -ml-3 mb-3"
          data-testid="wizard-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Volver
        </Button>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
          Nuevo Trabajador
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Paso {step + 1} de {STEPS.length} · {STEPS[step]?.label}
        </p>
      </div>

      {/* Stepper */}
      <Stepper steps={STEPS} current={step} />

      {/* Step panel */}
      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] mt-8">
        <CardContent className="p-6 md:p-8">
          {STEPS[step]?.key === "personal" && (
            <StepPersonal
              form={form}
              patch={patch}
              catalogs={catalogs}
              onPickPhoto={onPickPhoto}
              uploading={uploading}
              isOccasional={isOccasional}
            />
          )}
          {STEPS[step]?.key === "contrato" && (
            <StepContract
              form={form}
              patch={patch}
              catalogs={catalogs}
              branches={branches}
              companies={companies}
              departments={departments}
              occupations={occupations}
              isOccasional={isOccasional}
            />
          )}
          {STEPS[step]?.key === "condiciones" && (
            <StepConditions
              form={form}
              patch={patch}
              catalogs={catalogs}
              schedules={schedules}
              toggleWorkDay={toggleWorkDay}
            />
          )}
          {STEPS[step]?.key === "salario" && (
            <StepPayment
              form={form}
              patch={patch}
              catalogs={catalogs}
              occupations={occupations}
              payrollCfg={payrollCfg}
            />
          )}

          {/* Nav */}
          <div className="flex items-center justify-between pt-8 mt-8 border-t border-zinc-100">
            <Button
              variant="outline"
              onClick={onPrev}
              disabled={step === 0 || saving}
              data-testid="wizard-prev-button"
            >
              <ChevronLeft className="w-4 h-4 mr-1.5" /> Anterior
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                onClick={onNext}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="wizard-next-button"
              >
                Siguiente <ChevronRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button
                onClick={onSubmit}
                disabled={saving}
                className="bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="wizard-submit-button"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Guardando..." : "Guardar Trabajador"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Stepper
// ──────────────────────────────────────────────────────────────────────────
function Stepper({ steps, current }) {
  return (
    <div className="flex items-center justify-between gap-2 sm:gap-4">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s.key} className="flex-1 flex items-center">
            <div className="flex flex-col items-center gap-2 min-w-0">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all
                  ${done ? "bg-zinc-900 text-white" : active ? "bg-zinc-900 text-white ring-4 ring-zinc-100" : "bg-zinc-100 text-zinc-400 border border-zinc-200"}`}
              >
                {done ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span
                className={`text-[11px] sm:text-xs font-medium text-center truncate max-w-[10rem]
                  ${active ? "text-zinc-900" : done ? "text-zinc-700" : "text-zinc-400"}`}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-[2px] mx-1 sm:mx-3 rounded-full ${i < current ? "bg-zinc-900" : "bg-zinc-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 1 — Datos Personales
// ──────────────────────────────────────────────────────────────────────────
function StepPersonal({ form, patch, catalogs, onPickPhoto, uploading, isOccasional }) {
  const workerTypes = catalogs["worker-types"] || [];
  const idTypes = catalogs["id-types"] || [];
  const nationalities = catalogs.nationalities || [];
  const genders = catalogs.genders || [];
  const maritals = catalogs["marital-statuses"] || [];
  const educations = catalogs["education-levels"] || [];
  const relationships = catalogs.relationships || [];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-950">
        {isOccasional ? "Datos básicos" : "Datos Personales"}
      </h2>

      {/* Photo + worker type + fecha */}
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <div className="flex items-center gap-4 shrink-0">
          <label className="relative w-20 h-20 rounded-full overflow-hidden bg-zinc-100 border border-zinc-200 cursor-pointer flex items-center justify-center text-zinc-400 text-2xl font-semibold" data-testid="wizard-photo">
            {form.photo_path ? (
              <AuthImage path={form.photo_path} alt="foto" className="w-full h-full object-cover" />
            ) : (
              <Camera className="w-6 h-6 opacity-50" />
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              className="hidden"
              onChange={onPickPhoto}
              disabled={uploading}
              data-testid="wizard-photo-input"
            />
            {uploading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center text-[10px] text-zinc-700">
                Subiendo…
              </div>
            )}
          </label>
          <div className="text-xs text-zinc-500">
            <p className="font-semibold text-zinc-700 mb-0.5">Foto de perfil (opcional)</p>
            <p>JPG, PNG o GIF — máx. 5 MB</p>
          </div>
        </div>
      </div>

      {/* Worker type */}
      <div>
        <Label className="text-xs font-semibold text-zinc-700 mb-1.5 block">
          Tipo de trabajador *
        </Label>
        <Select value={form.worker_type} onValueChange={(v) => patch({ worker_type: v })}>
          <SelectTrigger data-testid="wizard-worker-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {workerTypes.map((w) => (
              <SelectItem key={w.code} value={w.code}>{w.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-zinc-400 mt-1.5">
          Fecha de registro: <span className="font-mono text-zinc-600">{formatDate(new Date())}</span>
        </p>
      </div>

      {/* Name */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label={`Nombre ${isOccasional ? "" : "*"}`}>
          <Input
            value={form.first_name}
            onChange={(e) => patch({ first_name: e.target.value })}
            placeholder="Nombre"
            data-testid="wizard-first-name-input"
          />
        </Field>
        <Field label={`Primer Apellido ${isOccasional ? "" : "*"}`}>
          <Input
            value={form.last_name_1}
            onChange={(e) => patch({ last_name_1: e.target.value })}
            placeholder="Primer Apellido"
            data-testid="wizard-last-name-1-input"
          />
        </Field>
        <Field label="Segundo Apellido">
          <Input
            value={form.last_name_2}
            onChange={(e) => patch({ last_name_2: e.target.value })}
            placeholder="Segundo Apellido"
            data-testid="wizard-last-name-2-input"
          />
        </Field>
      </div>

      {isOccasional ? (
        <p className="text-sm text-zinc-500">
          Para trabajadores <strong>ocasionales</strong> solo se requiere el nombre y la ocupación.
          Puede completar los demás datos más tarde.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tipo de Identificación *">
              <CatalogSelect
                value={form.id_type}
                options={idTypes}
                onChange={(v) => patch({ id_type: v })}
                testid="wizard-id-type-select"
              />
            </Field>
            <Field label="Número de Identificación *">
              <Input
                value={form.national_id}
                onChange={(e) => patch({ national_id: e.target.value })}
                placeholder="Número de identificación"
                data-testid="wizard-national-id-input"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Fecha de Nacimiento *">
              <DatePickerEs
                value={form.birth_date}
                onChange={(iso) => patch({ birth_date: iso })}
                placeholder="Seleccione fecha"
                testId="wizard-birth-date-input"
              />
            </Field>
            <Field label="Nacionalidad *">
              <CatalogSelect
                value={form.nationality}
                options={nationalities}
                onChange={(v) => patch({ nationality: v })}
                placeholder="Selecciona"
                testid="wizard-nationality-select"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <Field label="Lugar de Nacimiento">
              <Input
                value={form.birth_place || ""}
                onChange={(e) => patch({ birth_place: e.target.value })}
                placeholder="Ej: Quepos, San José"
                data-testid="wizard-birth-place-input"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Sexo *">
              <CatalogSelect
                value={form.gender}
                options={genders}
                onChange={(v) => patch({ gender: v })}
                placeholder="Selecciona"
                testid="wizard-gender-select"
              />
            </Field>
            <Field label="Estado Civil *">
              <CatalogSelect
                value={form.marital_status}
                options={maritals}
                onChange={(v) => patch({ marital_status: v })}
                testid="wizard-marital-select"
              />
            </Field>
            <Field label="Escolaridad *">
              <CatalogSelect
                value={form.education_level}
                options={educations}
                onChange={(v) => patch({ education_level: v })}
                placeholder="Selecciona"
                testid="wizard-education-select"
              />
            </Field>
          </div>

          <Field label="Lugar de Residencia Actual *">
            <Input
              value={form.address}
              onChange={(e) => patch({ address: e.target.value })}
              placeholder="Dirección completa"
              data-testid="wizard-address-input"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Teléfono *">
              <Input
                value={form.phone}
                onChange={(e) => patch({ phone: e.target.value })}
                placeholder="Teléfono"
                data-testid="wizard-phone-input"
              />
            </Field>
            <Field label="Correo Electrónico *">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => patch({ email: e.target.value })}
                placeholder="correo@ejemplo.com"
                data-testid="wizard-email-input"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nombre Persona de Contacto (Emergencia)">
              <Input
                value={form.emergency_contact_name}
                onChange={(e) => patch({ emergency_contact_name: e.target.value })}
                placeholder="Nombre completo"
                data-testid="wizard-emergency-name-input"
              />
            </Field>
            <Field label="Parentesco">
              <CatalogSelect
                value={form.emergency_contact_relationship}
                options={relationships}
                onChange={(v) => patch({ emergency_contact_relationship: v })}
                testid="wizard-relationship-select"
              />
            </Field>
          </div>
          <Field label="Teléfono Contacto de Emergencia">
            <Input
              value={form.emergency_contact_phone}
              onChange={(e) => patch({ emergency_contact_phone: e.target.value })}
              placeholder="Teléfono"
              data-testid="wizard-emergency-phone-input"
            />
          </Field>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 2 — Naturaleza del Contrato (or "Ocupación" for ocasional)
// ──────────────────────────────────────────────────────────────────────────
function StepContract({ form, patch, catalogs, branches, companies, departments, occupations, isOccasional }) {
  const contractTypes = catalogs["contract-types"] || [];
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-950">
        {isOccasional ? "Ocupación" : "Naturaleza del Contrato"}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Fecha de Ingreso *">
          <DatePickerEs
            value={form.hire_date}
            onChange={(iso) => patch({ hire_date: iso })}
            placeholder="Seleccione fecha"
            allowClear={false}
            testId="wizard-hire-date-input"
          />
        </Field>
        <Field label="Lugar de Trabajo *">
          <Select
            value={form.primary_branch_id}
            onValueChange={(v) => patch({ primary_branch_id: v })}
          >
            <SelectTrigger data-testid="wizard-branch-select">
              <SelectValue placeholder="Seleccionar sucursal" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Empresa *">
          <Select
            value={form.company_id || "none"}
            onValueChange={(v) => patch({ company_id: v === "none" ? "" : v })}
          >
            <SelectTrigger data-testid="wizard-company-select">
              <SelectValue placeholder="Seleccionar empresa" />
            </SelectTrigger>
            <SelectContent>
              {companies.length === 0 ? (
                <SelectItem value="none" disabled>Configura una empresa primero</SelectItem>
              ) : (
                companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.legal_name} {c.is_primary ? "★" : ""}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Ocupación *">
          <Select
            value={form.occupation_id}
            onValueChange={(v) => {
              const occ = occupations.find((o) => o.id === v);
              patch({
                occupation_id: v,
                ...(occ
                  ? {
                      base_salary: occ.base_salary ?? 0,
                      min_salary: occ.base_salary ?? 0,
                      occupation_salary_type: occ.salary_type || "monthly",
                      salary_fixation: occ.salary_type || "monthly",
                    }
                  : {}),
              });
            }}
          >
            <SelectTrigger data-testid="wizard-occupation-select">
              <SelectValue placeholder="Seleccionar ocupación" />
            </SelectTrigger>
            <SelectContent>
              {occupations.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}{o.acronym ? ` (${o.acronym})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {!isOccasional && (
          <Field label="Departamento">
            <Select
              value={form.department_id || "none"}
              onValueChange={(v) => patch({ department_id: v === "none" ? "" : v })}
            >
              <SelectTrigger data-testid="wizard-department-select">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin departamento</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}
      </div>

      {!isOccasional && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tipo de Contrato *">
              <CatalogSelect
                value={form.contract_type}
                options={contractTypes}
                onChange={(v) => patch({ contract_type: v })}
                testid="wizard-contract-type-select"
              />
            </Field>
          </div>
          {form.contract_type !== "CTI" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Motivo / Obra que justifica el plazo *">
                <Input
                  value={form.contract_reason || ""}
                  onChange={(e) => patch({ contract_reason: e.target.value })}
                  placeholder={form.contract_type === "OD" ? "Ej: Construcción de bodega" : "Ej: Sustituir vacaciones"}
                  data-testid="wizard-contract-reason-input"
                />
              </Field>
              <Field label="Fecha de finalización">
                <DatePickerEs
                  value={form.contract_end_date || ""}
                  onChange={(iso) => patch({ contract_end_date: iso })}
                  placeholder="Seleccione fecha"
                  testId="wizard-contract-end-date-input"
                />
              </Field>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 3 — Condiciones Laborales
// ──────────────────────────────────────────────────────────────────────────
function StepConditions({ form, patch, catalogs, schedules, toggleWorkDay }) {
  const journeyTypes = catalogs["journey-types"] || [];
  const journeyModes = catalogs["journey-modes"] || [];
  const scheduleTypes = catalogs["work-schedule-types"] || [];
  const weekDays = catalogs["week-days"] || [];

  const sg = form.social_guarantees || { ccss: true, ins: true };
  const setSG = (p) => patch({ social_guarantees: { ...sg, ...p } });

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-950">Condiciones Laborales</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Jornada Laboral *">
          <CatalogSelect
            value={form.journey_type}
            options={journeyTypes}
            onChange={(v) => patch({ journey_type: v })}
            testid="wizard-journey-type-select"
          />
        </Field>
        <Field
          label="Manera de Jornada Laboral *"
          hint="Continua: El colaborador trabaja en un solo bloque con pausas cortas para alimentación. Este no puede salir libremente del centro de trabajo y sigue bajo subordinación legal si ocurre una emergencia. — Discontinua (Fraccionada): la jornada se divide en dos o más bloques y aplica si el colaborador se desliga totalmente de la empresa y es libre de salir de las instalaciones."
        >
          <CatalogSelect
            value={form.journey_mode}
            options={journeyModes}
            onChange={(v) => patch({ journey_mode: v })}
            testid="wizard-journey-mode-select"
          />
        </Field>
      </div>

      <div>
        <Label className="text-xs font-medium text-zinc-700 mb-2 block">
          Garantías Sociales * <span className="text-zinc-400">(Obligatorias por Ley)</span>
        </Label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={sg.ccss}
              onCheckedChange={(v) => setSG({ ccss: !!v })}
              data-testid="wizard-ccss-checkbox"
            />
            <span className="text-sm text-zinc-800 font-medium">CCSS</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={sg.ins}
              onCheckedChange={(v) => setSG({ ins: !!v })}
              data-testid="wizard-ins-checkbox"
            />
            <span className="text-sm text-zinc-800 font-medium">INS</span>
          </label>
        </div>
      </div>

      <Field label="Horario de Trabajo *">
        <CatalogSelect
          value={form.work_schedule_type}
          options={scheduleTypes}
          onChange={(v) => patch({ work_schedule_type: v })}
          testid="wizard-schedule-type-select"
        />
      </Field>

      {schedules.length > 0 && (
        <Field label="Jornada configurada (opcional)">
          <Select
            value={form.schedule_id || "none"}
            onValueChange={(v) => patch({ schedule_id: v === "none" ? "" : v })}
          >
            <SelectTrigger data-testid="wizard-schedule-select">
              <SelectValue placeholder="Sin jornada específica" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Ninguna</SelectItem>
              {schedules.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name} ({s.start_time}-{s.end_time})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}

      <WorkScheduleEditor
        workDays={form.work_days}
        daySchedules={form.day_schedules || {}}
        weekDays={weekDays}
        journeyType={form.journey_type}
        accepted={form.schedule_accepted}
        onAcceptedChange={(v) => patch({ schedule_accepted: v })}
        onChange={(v) => patch(v)}
      />

      {/* Tiempos de descanso */}
      <div className="bg-zinc-50 rounded-lg p-4 border border-zinc-200">
        <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-600 mb-1 block">
          Tiempos de descanso (intrajornal)
        </Label>
        <p className="text-[11px] text-zinc-500 mb-3">
          Indique la duración en minutos de cada descanso (el horario es variable por trabajador).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { key: "breakfast", label: "Desayuno" },
            { key: "lunch", label: "Almuerzo" },
            { key: "refrigerio", label: "Refrigerio" },
          ].map((b) => {
            const cur = (form.break_schedule || {})[b.key] || { minutes: 0 };
            const update = (val) => {
              const next = {
                ...(form.break_schedule || {}),
                [b.key]: { minutes: val === "" ? "" : Math.max(0, Number(val) || 0) },
              };
              patch({ break_schedule: next });
            };
            return (
              <div key={b.key} className="space-y-1">
                <p className="text-[11px] font-medium text-zinc-700">{b.label}</p>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min="0"
                    step="5"
                    value={cur.minutes ?? 0}
                    onChange={(e) => update(e.target.value)}
                    className="h-8 text-xs"
                    placeholder="0"
                    data-testid={`wizard-break-${b.key}-minutes`}
                  />
                  <span className="text-zinc-400 text-xs whitespace-nowrap">min</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Step 4 — Salario y Forma de Pago
// ──────────────────────────────────────────────────────────────────────────
function StepPayment({ form, patch, catalogs, occupations = [], payrollCfg = {} }) {
  const paymentMethods = catalogs["payment-methods"] || [];
  const periodicities = catalogs["payment-periodicities"] || [];
  const selectedOcc = occupations.find((o) => o.id === form.occupation_id);
  const salaryType = form.occupation_salary_type || "monthly";
  const [otherInput, setOtherInput] = useState("");
  const otherList = form.other_deductions || [];
  const showOther = (form.authorized_deductions || []).includes("OTRO");
  const addOther = () => {
    const v = otherInput.trim();
    if (!v) return;
    if (otherList.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setOtherInput("");
      return;
    }
    patch({ other_deductions: [...otherList, v] });
    setOtherInput("");
  };
  const removeOther = (idx) => {
    patch({ other_deductions: otherList.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-zinc-950">Salario y Forma de Pago</h2>

      {/* Salario jalado desde la ocupación asignada */}
      <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-600">
            Salario según ocupación
          </p>
          {selectedOcc ? (
            <span className="text-xs text-zinc-500">
              {selectedOcc.name}
              {selectedOcc.acronym ? ` (${selectedOcc.acronym})` : ""} ·{" "}
              {salaryType === "daily" ? "Diaria" : "Mensual"}
            </span>
          ) : (
            <span className="text-xs text-amber-600">
              Seleccione una ocupación en el paso anterior
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={salaryType === "daily" ? "Salario base diario (₡)" : "Salario base mensual (₡)"}>
            <Input
              type="number"
              value={form.base_salary}
              onChange={(e) => patch({ base_salary: e.target.value })}
              placeholder="0.00"
              data-testid="wizard-base-salary-input"
            />
          </Field>
        </div>
        {Number(form.base_salary) > 0 && (
          <SalaryBreakdown
            amount={form.base_salary}
            salaryType={salaryType}
            cfg={payrollCfg}
            testid="wizard-salary-breakdown"
          />
        )}
        {Number(form.base_salary) > 0 && (
          <OvertimeBreakdown
            amount={form.base_salary}
            salaryType={salaryType}
            cfg={payrollCfg}
            daySchedules={form.day_schedules}
            journeyType={form.journey_type}
            testid="wizard-overtime-breakdown"
          />
        )}
        {Number(form.base_salary) > 0 && (
          <MonthlyPayEstimate
            amount={form.base_salary}
            salaryType={salaryType}
            cfg={payrollCfg}
            daySchedules={form.day_schedules}
            journeyType={form.journey_type}
            testid="wizard-monthly-pay-estimate"
          />
        )}
        <p className="text-[11px] text-zinc-500">
          El salario se toma de la ocupación asignada. Puede ajustarlo si este
          trabajador tiene un acuerdo distinto.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Fijación Salarial">
          <div
            className="h-9 px-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-700"
            data-testid="wizard-salary-fixation-display"
          >
            {salaryType === "daily" ? "Diaria" : "Mensual"}
            <span className="ml-auto text-[10px] text-zinc-400">según ocupación</span>
          </div>
        </Field>
        <Field label="Forma de Pago *">
          <CatalogSelect
            value={form.payment_method}
            options={paymentMethods}
            onChange={(v) => patch({ payment_method: v })}
            testid="wizard-payment-method-select"
          />
        </Field>
      </div>

      <Field label="Periodicidad de Pago *">
        <CatalogSelect
          value={form.payment_periodicity}
          options={periodicities}
          onChange={(v) => patch({ payment_periodicity: v })}
          testid="wizard-periodicity-select"
        />
      </Field>

      {form.payment_method === "TRB" && (
        <Field label="Cuenta Bancaria / IBAN">
          <Input
            value={form.bank_account}
            onChange={(e) => patch({ bank_account: e.target.value })}
            placeholder="CR00 0000 0000 0000 0000"
            data-testid="wizard-bank-account-input"
          />
        </Field>
      )}

      {/* Deducciones autorizadas */}
      <div className="bg-zinc-50 rounded-lg p-4 border border-zinc-200">
        <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-600 mb-2 block">
          Deducciones autorizadas
        </Label>
        <p className="text-[11px] text-zinc-500 mb-3">
          Marque los conceptos por los que el trabajador autoriza descuento de salario (art. 173 CT).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { value: "VALES", label: "Vales / Anticipos" },
            { value: "GARANTIAS_SOCIALES", label: "Garantías sociales (CCSS, INS)" },
            { value: "SUSPENCIONES", label: "Suspensiones disciplinarias" },
            { value: "OTRO", label: "Otras deducciones autorizadas" },
          ].map((d) => {
            const list = form.authorized_deductions || [];
            const checked = list.includes(d.value);
            const toggle = () => {
              const next = checked ? list.filter((x) => x !== d.value) : [...list, d.value];
              patch({ authorized_deductions: next });
            };
            return (
              <label
                key={d.value}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-white cursor-pointer text-xs text-zinc-700"
                data-testid={`wizard-deduction-${d.value}`}
              >
                <input type="checkbox" checked={checked} onChange={toggle} />
                {d.label}
              </label>
            );
          })}
        </div>

        {showOther && (
          <div className="mt-3 pt-3 border-t border-zinc-200" data-testid="wizard-other-deductions">
            <Label className="text-[11px] font-medium text-zinc-700 mb-1.5 block">
              Detalle de otras deducciones autorizadas
            </Label>
            <div className="flex items-center gap-2">
              <Input
                value={otherInput}
                onChange={(e) => setOtherInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addOther();
                  }
                }}
                placeholder="Ej: Préstamo cooperativa, Asociación solidarista"
                className="h-8 text-xs"
                data-testid="wizard-other-deduction-input"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addOther}
                className="h-8 px-3 text-xs shrink-0"
                data-testid="wizard-other-deduction-add"
              >
                Agregar
              </Button>
            </div>
            {otherList.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {otherList.map((d, i) => (
                  <span
                    key={`${d}-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-zinc-300 pl-2.5 pr-1 py-0.5 text-xs text-zinc-700"
                    data-testid={`wizard-other-deduction-chip-${i}`}
                  >
                    {d}
                    <button
                      type="button"
                      onClick={() => removeOther(i)}
                      className="rounded-full hover:bg-zinc-100 w-4 h-4 flex items-center justify-center text-zinc-400 hover:text-zinc-700"
                      aria-label={`Quitar ${d}`}
                      data-testid={`wizard-other-deduction-remove-${i}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Small helpers
// ──────────────────────────────────────────────────────────────────────────
function Field({ label, children, hint }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Label className="text-xs font-semibold text-zinc-700 block">{label}</Label>
        {hint ? <HelpHint text={hint} /> : null}
      </div>
      {children}
    </div>
  );
}

function HelpHint({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Ayuda"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onClick={() => setOpen((o) => !o)}
          className="text-zinc-400 hover:text-zinc-700 transition-colors"
          data-testid="journey-mode-help"
        >
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className="w-72 text-xs leading-relaxed text-zinc-700"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

function CatalogSelect({ value, options, onChange, placeholder = "Selecciona", testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
